/**
 * Time-zone helpers. Instants are stored in UTC; every calendar decision (what is
 * "today", when a date-only reminder fires) uses the user's profile time zone.
 * Nothing here assumes a particular zone.
 */
import { TZDate } from '@date-fns/tz';
import type { ISODate, ISOTime, Instant } from './types.ts';

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;
const DAY_MS = 86_400_000;

export interface DateParts {
  year: number;
  month: number; // 1-12
  day: number;
}

export interface TimeParts {
  hour: number;
  minute: number;
  second: number;
}

export function parseISODate(date: ISODate): DateParts {
  const m = DATE_RE.exec(date);
  if (!m) throw new Error(`Invalid date: ${date}`);
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

export function isISODate(value: unknown): value is ISODate {
  if (typeof value !== 'string' || !DATE_RE.test(value)) return false;
  const { year, month, day } = parseISODate(value);
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}

export function formatISODate(year: number, month: number, day: number): ISODate {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function parseTime(time: ISOTime): TimeParts {
  const m = TIME_RE.exec(time);
  if (!m) throw new Error(`Invalid time: ${time}`);
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  const second = m[3] ? Number(m[3]) : 0;
  if (hour > 23 || minute > 59 || second > 59) throw new Error(`Invalid time: ${time}`);
  return { hour, minute, second };
}

/** `09:00:00` → `09:00`. */
export function normalizeTime(time: ISOTime): ISOTime {
  const { hour, minute } = parseTime(time);
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function toDate(instant: Date | Instant | number): Date {
  return instant instanceof Date ? instant : new Date(instant);
}

/** Calendar date of an instant as seen in `tz`. */
export function toLocalDate(instant: Date | Instant | number, tz: string): ISODate {
  const z = new TZDate(toDate(instant).getTime(), tz);
  return formatISODate(z.getFullYear(), z.getMonth() + 1, z.getDate());
}

/** Wall-clock time (`HH:mm`) of an instant as seen in `tz`. */
export function toLocalTime(instant: Date | Instant | number, tz: string): ISOTime {
  const z = new TZDate(toDate(instant).getTime(), tz);
  return `${String(z.getHours()).padStart(2, '0')}:${String(z.getMinutes()).padStart(2, '0')}`;
}

/** Full local wall-clock parts of an instant in `tz`, including seconds and milliseconds. */
export function toLocalParts(
  instant: Date | Instant | number,
  tz: string,
): DateParts & TimeParts & { millisecond: number; weekday: number } {
  const z = new TZDate(toDate(instant).getTime(), tz);
  return {
    year: z.getFullYear(),
    month: z.getMonth() + 1,
    day: z.getDate(),
    hour: z.getHours(),
    minute: z.getMinutes(),
    second: z.getSeconds(),
    millisecond: z.getMilliseconds(),
    weekday: z.getDay(),
  };
}

const offsetFormatters = new Map<string, Intl.DateTimeFormat>();

/** Minutes the wall clock in `tz` is ahead of UTC at instant `ms`. Independent of the host zone. */
export function zoneOffsetMinutes(ms: number, tz: string): number {
  let f = offsetFormatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    offsetFormatters.set(tz, f);
  }
  const p: Record<string, number> = {};
  for (const part of f.formatToParts(new Date(ms))) {
    if (part.type !== 'literal') p[part.type] = Number(part.value);
  }
  const wall = Date.UTC(p.year!, p.month! - 1, p.day!, p.hour! % 24, p.minute!, p.second!);
  return Math.round((wall - Math.floor(ms / 1000) * 1000) / 60_000);
}

/**
 * The instant at which the wall clock in `tz` shows `date` `time`, computed the same way on
 * every machine (and the same way Postgres does):
 *  - a time inside a DST gap moves forward (01:30 on a spring-forward night becomes 02:30);
 *  - a time that happens twice (fall back) resolves to the first occurrence.
 */
export function localDateTimeToInstant(date: ISODate, time: ISOTime, tz: string): Date {
  const { year, month, day } = parseISODate(date);
  const { hour, minute, second } = parseTime(time);
  const wall = Date.UTC(year, month - 1, day, hour, minute, second);
  const offsets = new Set([
    zoneOffsetMinutes(wall, tz),
    zoneOffsetMinutes(wall - 86_400_000, tz),
    zoneOffsetMinutes(wall + 86_400_000, tz),
  ]);
  const candidates = [...offsets]
    .map((off) => wall - off * 60_000)
    .filter((t) => wall - zoneOffsetMinutes(t, tz) * 60_000 === t)
    .sort((a, b) => a - b);
  if (candidates.length > 0) return new Date(candidates[0]!);
  // In a gap: use the offset in force just before the transition, which lands after it.
  const before = zoneOffsetMinutes(wall - 86_400_000, tz);
  return new Date(wall - before * 60_000);
}

/** The instant of local midnight at the start of `date` in `tz`. */
export function startOfDay(date: ISODate, tz: string): Date {
  return localDateTimeToInstant(date, '00:00', tz);
}

/** Today's calendar date in `tz`. */
export function todayInTz(tz: string, now: Date = new Date()): ISODate {
  return toLocalDate(now, tz);
}

/** Pure calendar arithmetic, independent of any time zone or DST. */
export function addDays(date: ISODate, days: number): ISODate {
  const { year, month, day } = parseISODate(date);
  const d = new Date(Date.UTC(year, month - 1, day + days));
  return formatISODate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/** Whole calendar days from `b` to `a` (positive when `a` is later). */
export function diffDays(a: ISODate, b: ISODate): number {
  const pa = parseISODate(a);
  const pb = parseISODate(b);
  return Math.round(
    (Date.UTC(pa.year, pa.month - 1, pa.day) - Date.UTC(pb.year, pb.month - 1, pb.day)) / DAY_MS,
  );
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(date: ISODate): number {
  const { year, month, day } = parseISODate(date);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

export function addMonthsClamped(date: ISODate, months: number): ISODate {
  const { year, month, day } = parseISODate(date);
  const target = new Date(Date.UTC(year, month - 1 + months, 1));
  const ty = target.getUTCFullYear();
  const tm = target.getUTCMonth() + 1;
  return formatISODate(ty, tm, Math.min(day, daysInMonth(ty, tm)));
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function isWeekend(date: ISODate): boolean {
  const dow = dayOfWeek(date);
  return dow === 0 || dow === 6;
}

/** Start (inclusive) of the week containing `date`; `weekStartsOn` 0 = Sunday, 1 = Monday. */
export function startOfWeek(date: ISODate, weekStartsOn: 0 | 1 = 1): ISODate {
  const dow = dayOfWeek(date);
  const delta = (dow - weekStartsOn + 7) % 7;
  return addDays(date, -delta);
}

export function compareISODate(a: ISODate, b: ISODate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * The instant a task is due.
 * Timed tasks: `due_date due_time` in `tz`. Date-only tasks: `due_date` at `dateOnlyTime`
 * (the user's date-only reminder time, which defaults to "My day starts at").
 * Must stay in sync with the SQL function `public.compute_task_due_at`.
 */
export function computeDueAt(
  dueDate: ISODate | null,
  dueTime: ISOTime | null,
  tz: string,
  dateOnlyTime: ISOTime,
): Instant | null {
  if (!dueDate) return null;
  return localDateTimeToInstant(dueDate, dueTime ?? dateOnlyTime, tz).toISOString();
}

/** `date` at the user's "My day starts at" time on the following day, e.g. "Tomorrow morning". */
export function nextMorning(now: Date, tz: string, dayStartTime: ISOTime): Date {
  return localDateTimeToInstant(addDays(todayInTz(tz, now), 1), dayStartTime, tz);
}

export function addMinutes(instant: Date | Instant, minutes: number): Date {
  return new Date(toDate(instant).getTime() + minutes * 60_000);
}
